import {DataTable} from "../DataTable/DataTable";
import {ObjectComboMapping} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {
  useObjectComboMappingDataTableFilter
} from "../ObjectComboMappingDataTable/useObjectComboMappingDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {type ReactNode, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";

const EMPTY_ARRAY: ObjectComboMapping[] = []

export type BaseObjectComboMappingDataTableProps = EntityDataTableProps<ObjectComboMapping> & {
  renderActions: (record: ObjectComboMapping, index: number) => ReactNode
}

export const BaseObjectComboMappingDataTable = (props: BaseObjectComboMappingDataTableProps) => {
  const {
    records: mappings = EMPTY_ARRAY,
    recordsPerPageOptions,
    renderActions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useObjectComboMappingDataTableFilter({
    records: mappings
  })

  const sortConfig = useMemo(() => {
    return {
      "leftObjectName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.leftObjectName?.toLowerCase()
          }])
        }
      },
      "rightObjectName": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.rightObjectName?.toLowerCase()
          }])
        }
      },
      "key": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.key?.toLowerCase()
          }])
        }
      },
      "value": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.value?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'key', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<ObjectComboMapping>[] = [
      {
        ...getColumnFilterProps(filterConfig, "leftObjectName"),
        title: t("common.entity.objectcombomapping.table.column.leftobject", "Left Object"),
        accessor: 'leftObjectName',
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "rightObjectName"),
        title: t("common.entity.objectcombomapping.table.column.rightobject", "Right Object"),
        accessor: 'rightObjectName',
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "key"),
        title: t("common.entity.objectcombomapping.table.column.key", "Key"),
        accessor: "key",
        width: 100,
      },
      {
        ...getColumnFilterProps(filterConfig, "value"),
        title: t("common.entity.objectcombomapping.table.column.value", "Value"),
        accessor: "value",
        width: 100
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: renderActions,
      }
    ]

    return values
  }, [filterConfig, renderActions, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
